import { NgForOf } from '@angular/common';
import { Component, inject, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { merge, of as observableOf } from 'rxjs';
import { catchError, map, startWith, switchMap } from 'rxjs/operators';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { RouterLink } from '@angular/router';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CustomizerSettingsService } from '../../customizer-settings/customizer-settings.service';
import { ApiService } from '../../services/api/api.service';
import { Brand } from '../../interfaces/Brand';
import { BrandResponse } from '../../interfaces/BrandResponse';
import { TranslocoModule } from '@ngneat/transloco';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Company } from '../../interfaces/Company';
import { CompanyResponse } from '../../interfaces/CompanyResponse';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from '../../common/confirm-dialog/confirn-dialog.component';
import { HttpErrorResponse } from '@angular/common/http';
import { AlertDialogComponent } from '../../common/alert-dialog/alert-dialog.component';

@Component({
    selector: 'app-m-brands',
    standalone: true,
    imports: [
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        MatCardModule,
        MatMenuModule,
        MatButtonModule,
        RouterLink,
        MatTableModule,
        MatSortModule,
        MatPaginatorModule,
        NgForOf,
        MatTooltipModule,
        TranslocoModule,
        MatProgressSpinnerModule,
    ],
    templateUrl: './m-brands.component.html',
    styleUrl: './m-brands.component.scss',
})
export class MBrandsComponent {
    displayedColumns: string[] = ['name', 'domain', 'companyName', 'action'];
    data: Brand[] = [];
    dataSource = new MatTableDataSource<Brand>(this.data);
    companies: Company[] = [];
    selectedCompany: string = '';
    totalItems = 0;
    pageSizes = [10, 20, 50, 1000];
    isLoadingResults = true;
    isRateLimitReached = false;

    readonly dialog = inject(MatDialog);

    @ViewChild(MatPaginator) paginator!: MatPaginator;
    @ViewChild(MatSort) sort!: MatSort;

    // isToggled
    isToggled = false;

    constructor(
        public themeService: CustomizerSettingsService,
        private apiService: ApiService
    ) {
        this.themeService.isToggled$.subscribe((isToggled) => {
            this.isToggled = isToggled;
        });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * ngOnInit
     */
    ngOnInit(): void {
        this.apiService
            .getCompanyPagination(0, 1000000, 'name', 'asc')
            .subscribe((response: CompanyResponse) => {
                this.companies.push(...response.companies);
                // If the user changes the sort order, reset back to the first page.
                this.sort.sortChange.subscribe(
                    () => (this.paginator.pageIndex = 0)
                );

                merge(this.sort.sortChange, this.paginator.page)
                    .pipe(
                        startWith({}),
                        switchMap(() => {
                            this.isLoadingResults = true;
                            if (this.selectedCompany) {
                                return this.apiService
                                    .getBrandByCompanyId(
                                        this.selectedCompany,
                                        this.paginator.pageIndex,
                                        this.paginator.pageSize,
                                        this.sort.active,
                                        this.sort.direction
                                    )
                                    .pipe(catchError(() => observableOf(null)));
                            } else {
                                return this.apiService
                                    .getBrand(
                                        this.paginator.pageIndex,
                                        this.paginator.pageSize,
                                        this.sort.active,
                                        this.sort.direction
                                    )
                                    .pipe(catchError(() => observableOf(null)));
                            }
                        }),
                        map((data) => {
                            // Flip flag to show that loading has finished.
                            this.isLoadingResults = false;
                            //this.isRateLimitReached = data === null;

                            if (data === null) {
                                return [];
                            } else {
                                data.brands.forEach((elem) => {
                                    const result = this.companies.find(
                                        ({ id }) => id === elem.companyId
                                    );
                                    if (result) {
                                        elem.companyName = result.name;
                                    }
                                });
                            }

                            // Only refresh the result length if there is new data. In case of rate
                            // limit errors, we do not want to reset the paginator to zero, as that
                            // would prevent users from re-triggering requests.
                            this.totalItems = data.totalElements;
                            return data.brands;
                        })
                    )
                    .subscribe((data) => (this.dataSource.data = data));
            });
    }

    // RTL Mode
    toggleRTLEnabledTheme() {
        this.themeService.toggleRTLEnabledTheme();
    }

    // Search Filter
    applyFilter(event: Event) {
        const filterValue = (event.target as HTMLInputElement).value;
        this.dataSource.filter = filterValue.trim().toLowerCase();

        if (this.dataSource.paginator) {
            this.dataSource.paginator.firstPage();
        }
    }

    // onKeydown
    onKeydown(event: Event) {
        //console.log(event);
        return false;
    }

    // Change company
    changeCompany(companyId: string) {
        this.isLoadingResults = true;
        this.paginator.pageIndex = 0;
        if (companyId && companyId.length > 0) {
            this.apiService
                .getBrandByCompanyId(
                    companyId,
                    this.paginator.pageIndex,
                    this.paginator.pageSize,
                    this.sort.active,
                    this.sort.direction
                )
                .subscribe((response: BrandResponse) => {
                    // Flip flag to show that loading has finished.
                    this.isLoadingResults = false;
                    if (response) {
                        response.brands.forEach((elem) => {
                            const result = this.companies.find(
                                ({ id }) => id === elem.companyId
                            );
                            if (result) {
                                elem.companyName = result.name;
                            }
                        });
                        this.totalItems = response.totalElements;
                        this.dataSource.data = response.brands;
                    } else {
                        this.totalItems = 0;
                        this.dataSource.data = [];
                    }
                });
        } else {
            this.apiService
                .getBrand(
                    this.paginator.pageIndex,
                    this.paginator.pageSize,
                    this.sort.active,
                    this.sort.direction
                )
                .subscribe((response: BrandResponse) => {
                    // Flip flag to show that loading has finished.
                    this.isLoadingResults = false;
                    if (response) {
                        response.brands.forEach((elem) => {
                            const result = this.companies.find(
                                ({ id }) => id === elem.companyId
                            );
                            if (result) {
                                elem.companyName = result.name;
                            }
                        });
                        this.totalItems = response.totalElements;
                        this.dataSource.data = response.brands;
                    } else {
                        this.totalItems = 0;
                        this.dataSource.data = [];
                    }
                });
        }
    }

    // Delete
    delete(id: string) {
        const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            data: {
                action: 'delete',
                component: 'brand',
                content: 'content-delete',
                buttonText: {
                    ok: 'yes',
                    cancel: 'no',
                },
            },
        });

        dialogRef.afterClosed().subscribe((confirmed: boolean) => {
            if (confirmed) {
                console.log('Delete Brand: ' + id);
                this.apiService.deleteBrand(id).subscribe({
                    next: (value: BrandResponse) => {
                        console.log(value);
                        this.selectedCompany = '';
                        this.changeCompany(this.selectedCompany);
                    },
                    error: (error) => {
                        const dialogRef = this.dialog.open(
                            AlertDialogComponent,
                            {
                                data: {
                                    title: 'ERROR',
                                    message: error,
                                },
                            }
                        );
                    },
                });
            }
        });
    }
}
